/**
 * Fetal Biometry Percentile Chart Component
 * 
 * Displays measurement on percentile growth curve
 * Professional medical visualization for gynecologists
 */

import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  ComposedChart,
  Scatter
} from 'recharts';
import { getPercentileCurve, calculatePercentile, MEASUREMENT_TO_PERCENTILE } from '../lib/fetal-percentiles';

/**
 * Single Measurement Percentile Chart
 */
export const PercentileChart = ({ 
  measurementType, 
  currentValue, 
  currentWeek,
  height = 200,
  showLegend = true 
}) => {
  const curveData = useMemo(() => getPercentileCurve(measurementType), [measurementType]);
  const mapping = MEASUREMENT_TO_PERCENTILE[measurementType];
  
  const percentileResult = useMemo(() => {
    return calculatePercentile(measurementType, currentValue, currentWeek);
  }, [measurementType, currentValue, currentWeek]);

  // Add current point to data
  const dataWithCurrent = useMemo(() => {
    return curveData.map(d => ({
      ...d,
      current: d.week === currentWeek ? currentValue : null
    }));
  }, [curveData, currentWeek, currentValue]);

  if (!mapping || curveData.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-slate-400 text-sm">
        Bu ölçüm için persentil verisi yok
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-xl">
          <p className="text-cyan-300 font-medium mb-1">{label}. Hafta</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-xs" style={{ color: entry.color }}>
              {entry.name}: {entry.value?.toFixed(1)} mm
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full">
      {/* Header with percentile info */}
      <div className="flex items-center justify-between mb-2 px-2">
        <div>
          <span className="text-sm font-medium text-slate-200">{mapping.name}</span>
          <span className="text-xs text-slate-400 ml-2">({mapping.nameEn})</span>
        </div>
        <div className="flex items-center gap-2">
          <span 
            className="text-lg font-bold"
            style={{ color: percentileResult.color }}
          >
            P{percentileResult.percentile.toFixed(0)}
          </span>
          <span 
            className="text-xs px-2 py-0.5 rounded-full"
            style={{ 
              backgroundColor: percentileResult.color + '20',
              color: percentileResult.color 
            }}
          >
            {percentileResult.interpretation}
          </span>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={dataWithCurrent} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <defs>
            <linearGradient id={`gradient-${measurementType}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#22c55e" stopOpacity={0.05}/>
            </linearGradient>
          </defs>
          
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
          
          <XAxis 
            dataKey="week" 
            stroke="#64748b"
            tick={{ fill: '#94a3b8', fontSize: 10 }}
            label={{ value: 'Hafta', position: 'insideBottomRight', offset: -5, fill: '#64748b', fontSize: 10 }}
          />
          
          <YAxis 
            stroke="#64748b"
            tick={{ fill: '#94a3b8', fontSize: 10 }}
            label={{ value: 'mm', angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 10 }}
          />
          
          <Tooltip content={<CustomTooltip />} />
          
          {showLegend && (
            <Legend 
              wrapperStyle={{ fontSize: '10px' }}
              formatter={(value) => <span style={{ color: '#94a3b8' }}>{value}</span>}
            />
          )}

          {/* Normal range area (P5-P95) */}
          <Area
            type="monotone"
            dataKey="p95"
            stroke="none"
            fill={`url(#gradient-${measurementType})`}
            name="P95"
          />

          {/* Percentile lines */}
          <Line 
            type="monotone" 
            dataKey="p5" 
            stroke="#ef4444" 
            strokeWidth={1}
            strokeDasharray="4 4"
            dot={false}
            name="P5"
          />
          
          <Line 
            type="monotone" 
            dataKey="p50" 
            stroke="#22c55e" 
            strokeWidth={2}
            dot={false}
            name="P50"
          />
          
          <Line 
            type="monotone" 
            dataKey="p95" 
            stroke="#ef4444" 
            strokeWidth={1}
            strokeDasharray="4 4"
            dot={false}
            name="P95"
          />

          {/* Current value point */}
          <Scatter
            dataKey="current"
            fill={percentileResult.color}
            shape={(props) => {
              if (!props.cx || !props.cy || props.payload.current === null) return null;
              return (
                <g>
                  <circle cx={props.cx} cy={props.cy} r={8} fill={percentileResult.color} opacity={0.3} />
                  <circle cx={props.cx} cy={props.cy} r={5} fill={percentileResult.color} stroke="#fff" strokeWidth={2} />
                </g>
              );
            }}
            name="Mevcut"
          />

          {/* Current week reference line */}
          <ReferenceLine 
            x={currentWeek} 
            stroke="#8b5cf6" 
            strokeDasharray="3 3"
            strokeWidth={1}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Percentile Summary Badge
 */
export const PercentileBadge = ({ measurementType, value, week }) => {
  const result = useMemo(() => {
    return calculatePercentile(measurementType, value, week);
  }, [measurementType, value, week]);

  const mapping = MEASUREMENT_TO_PERCENTILE[measurementType];
  if (!mapping) return null;

  return (
    <div 
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium"
      style={{ 
        backgroundColor: result.color + '15',
        color: result.color,
        border: `1px solid ${result.color}40`
      }}
    >
      <span>{mapping.name}</span>
      <span className="font-bold">P{result.percentile.toFixed(0)}</span>
    </div>
  );
};

/**
 * Multi-Measurement Percentile Summary
 */
export const PercentileSummary = ({ measurements, week }) => {
  const results = useMemo(() => {
    const summary = [];
    
    for (const [key, value] of Object.entries(measurements)) {
      if (MEASUREMENT_TO_PERCENTILE[key] && value) {
        const result = calculatePercentile(key, value, week);
        summary.push({
          key,
          name: MEASUREMENT_TO_PERCENTILE[key].name,
          value,
          ...result
        });
      }
    }
    
    return summary;
  }, [measurements, week]);

  return (
    <div className="space-y-2">
      <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wide">
        Persentil Özeti - {week}. Hafta
      </h4>
      <div className="grid grid-cols-2 gap-2">
        {results.map((result) => (
          <div 
            key={result.key}
            className="flex items-center justify-between p-2 rounded-lg bg-slate-800/50"
          >
            <div>
              <span className="text-xs text-slate-400">{result.name}</span>
              <p className="text-sm font-medium text-slate-200">
                {result.value?.toFixed(1)} mm
              </p>
            </div>
            <div 
              className="text-right"
              style={{ color: result.color }}
            >
              <span className="text-lg font-bold">P{result.percentile.toFixed(0)}</span>
              <p className="text-xs">{result.interpretation}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Percentile Range Indicator (mini visualization)
 */
export const PercentileRangeIndicator = ({ measurementType, value, week }) => {
  const result = useMemo(() => {
    return calculatePercentile(measurementType, value, week);
  }, [measurementType, value, week]);

  const position = Math.max(0, Math.min(100, result.percentile));

  return (
    <div className="w-full">
      <div className="relative h-2 bg-slate-700 rounded-full overflow-hidden">
        {/* Gradient background */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(90deg, #ef4444 0%, #f97316 15%, #22c55e 25%, #22c55e 75%, #f97316 85%, #ef4444 100%)'
          }}
        />
        
        {/* Position indicator */}
        <div 
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg border-2"
          style={{ 
            left: `calc(${position}% - 6px)`,
            borderColor: result.color
          }}
        />
      </div>
      
      {/* Labels */}
      <div className="flex justify-between mt-1 text-[9px] text-slate-500">
        <span>P5</span>
        <span>P50</span>
        <span>P95</span>
      </div>
    </div>
  );
};

export default PercentileChart;
